import { BOWL, BOWL_PIECE, type BowlPiece, type BowlPrim } from "./bowl";
import { BOX, BOX_PIECE, type BoxPiece, type BoxPrim } from "./box";
import { CHUTE, CHUTE_PIECE, type ChutePiece, type ChutePrim } from "./chute";
import { HUBBA, HUBBA_PIECE, type HubbaPiece, type HubbaPrim } from "./hubba";
import { HALFPIPE_PIECE, type HalfpipePiece } from "./halfpipe";
import { KICKER, KICKER_PIECE, type KickerPiece, type KickerPrim } from "./kicker";
import { QUARTER, QUARTER_PIECE, type QuarterPiece, type QuarterPrim } from "./quarter";
import { POOL, POOL_PIECE, type PoolPiece, type PoolPrim } from "./pool";
import { RAIL_PIECE, type RailPiece } from "./rail";
import { SCENERY_PIECE, type SceneryPiece } from "./scenery";
import { SPINE_PIECE, type SpinePiece } from "./spine";
import { STAIRS, STAIRS_PIECE, type StairsPiece, type StairsPrim } from "./stairs";
import type { PieceKind, PrimKind } from "./types";

// The registry of park primitives (what the heightfield is made of) and map
// pieces (what a map file lists; composite pieces like a halfpipe compile to
// several primitives). Adding a primitive: a file here with its PrimKind and
// PieceKind, a line in each list below, and its mesh in PRIM_MESHES
// (render/level-meshes.ts). The mapped types make a missing entry a compile
// error.

export type Prim = BoxPrim | KickerPrim | QuarterPrim | BowlPrim | StairsPrim | HubbaPrim | PoolPrim | ChutePrim;

export type MapPiece = BoxPiece | KickerPiece | QuarterPiece | HalfpipePiece | SpinePiece | StairsPiece | BowlPiece | RailPiece | SceneryPiece | HubbaPiece | PoolPiece | ChutePiece;

export const PRIMS: { readonly [K in Prim["kind"]]: PrimKind<Extract<Prim, { kind: K }>> } = {
  box: BOX,
  kicker: KICKER,
  quarter: QUARTER,
  bowl: BOWL,
  stairs: STAIRS,
  hubba: HUBBA,
  pool: POOL,
  chute: CHUTE,
};

/** In the order a "bad kind" error lists them. */
export const PIECES: { readonly [K in MapPiece["kind"]]: PieceKind<Extract<MapPiece, { kind: K }>> } = {
  box: BOX_PIECE,
  kicker: KICKER_PIECE,
  quarter: QUARTER_PIECE,
  halfpipe: HALFPIPE_PIECE,
  spine: SPINE_PIECE,
  stairs: STAIRS_PIECE,
  bowl: BOWL_PIECE,
  rail: RAIL_PIECE,
  scenery: SCENERY_PIECE,
  hubba: HUBBA_PIECE,
  pool: POOL_PIECE,
  chute: CHUTE_PIECE,
};

/** A primitive's kind, typed for that primitive (TypeScript can't narrow a mapped lookup by itself). */
export function primKind<P extends Prim>(p: P): PrimKind<P> {
  return PRIMS[p.kind] as unknown as PrimKind<P>;
}

/**
 * A primitive's footprint as rectangles in its local frame (u0, u1, w0, w1):
 * one, except an L pool's (#169), which is its two wings.
 */
export function primParts(p: Prim): [u0: number, u1: number, w0: number, w1: number][] {
  const [u0, u1] = primKind(p).extent(p as never);
  if (p.kind !== "pool" || !p.arm) return [[u0, u1, -p.hw, p.hw]];
  return primKind(p)
    .plan(p)
    .map((q) => [q.u0, q.u1, (q.w ?? 0) - (q.hw ?? p.hw), (q.w ?? 0) + (q.hw ?? p.hw)]);
}

/** The local w range a primitive covers: ±hw, or its own `across` (an L pool, #169). */
export function primAcross(p: Prim): [w0: number, w1: number] {
  return primKind(p).across?.(p as never) ?? [-p.hw, p.hw];
}

/** A map piece's kind, typed for that piece. */
export function pieceKind<T extends MapPiece>(p: T): PieceKind<T> {
  return PIECES[p.kind] as unknown as PieceKind<T>;
}
